import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Card, Typography, Spin, Alert, Divider } from 'antd';
import { UserOutlined, LockOutlined, SafetyCertificateOutlined, TeamOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import './AuthPage.css';

const { Title, Text, Paragraph } = Typography;

/**
 * Client Authentication page for signin/signup
 */
const ClientAuth = () => {
  const { user, isAuthenticated, isLoading, error, login, register, clearError, setError } = useAuth();
  const history = useHistory();
  const location = useLocation();

  // Redirect if already authenticated - go directly to dashboard
  useEffect(() => {
    if (isAuthenticated && user) {
      const nextUrl = new URLSearchParams(location.search).get('next');
      
      // If there's a specific next URL, use it
      if (nextUrl) {
        history.replace(nextUrl);
        return;
      }
      
      // For authenticated users, go directly to admin dashboard
      console.log('User is authenticated - redirecting to admin dashboard');
      history.replace('/admin');
    }
  }, [isAuthenticated, user, history, location]);

  // Handle callback errors
  useEffect(() => {
    const error = new URLSearchParams(location.search).get('error');
    if (error) {
      let errorMessage = 'Authentication failed. Please try again.';
      
      switch (error) {
        case 'missing_tokens':
          errorMessage = 'Authentication tokens were not received. Please try again.';
          break;
        case 'session_validation_failed':
          errorMessage = 'Session validation failed. Please try again.';
          break;
        case 'session_validation_error':
          errorMessage = 'Error validating your session. Please try again.';
          break;
        case 'callback_error':
          errorMessage = 'Error during authentication. Please try again.';
          break;
        default:
          errorMessage = `Authentication error: ${error}. Please try again.`;
      }
      
      // Set the error in the auth context
      clearError(); // Clear any existing error first
      setTimeout(() => {
        // Use setTimeout to ensure the error is set after the component mounts
        setError(errorMessage);
      }, 100);
    }
  }, [location, clearError, setError]);

  // Handle OIDC login with client role
  const handleOIDCLogin = () => {
    clearError();
    login('client');
  };

  // Handle OIDC register with client role
  const handleOIDCRegister = () => {
    clearError();
    register('client');
  };

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="auth-loading">
        <Spin size="large" />
        <div className="auth-loading-text">
          <Text>Verifying your session...</Text>
        </div>
      </div>
    );
  }

  // Show error if authentication failed
  if (error) {
    return (
      <div className="auth-container">
        <Card className="auth-card">
          <div className="auth-content">
            <div className="auth-header">
              <SafetyCertificateOutlined className="auth-icon error" />
              <Title level={2} className="auth-title">
                Authentication Error
              </Title>
            </div>
            
            <Alert
              message="Login Failed"
              description={error}
              type="error"
              showIcon
              className="auth-error"
            />
            
            <Button 
              type="primary" 
              size="large" 
              onClick={handleOIDCLogin}
              className="auth-button"
              icon={<UserOutlined />}
            >
              Try Again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Main OIDC signin/signup page
  return (
    <div className="auth-container">
      <Card className="auth-card">
        <div className="auth-content">
          <div className="auth-header">
            <TeamOutlined className="auth-icon client-icon" />
            <Title level={1} className="auth-title">
              Welcome Back
            </Title>
            <Text className="auth-subtitle">
              Sign in to access your dashboard
            </Text>
          </div>

          <div className="auth-actions">
            <Button 
              type="primary" 
              size="large" 
              onClick={handleOIDCLogin}
              className="auth-button oidc-button"
              icon={<LockOutlined />}
              loading={isLoading}
            >
              Sign in with OIDC
            </Button>
            
            <Divider>or</Divider>
            
            <Button 
              type="default" 
              size="large" 
              onClick={handleOIDCRegister}
              className="auth-button signup-button"
              icon={<UserOutlined />}
              loading={isLoading}
            >
              Create New Account
            </Button>
          </div>

          <div className="auth-info-section">
            <Paragraph className="auth-info">
              <SafetyCertificateOutlined /> Secure authentication powered by Stride ID
            </Paragraph>
            <Paragraph className="auth-info">
              You will be redirected to your identity provider for secure sign-in
            </Paragraph>
          </div>

          <div className="auth-footer">
            <Text type="secondary">
              Having trouble? Contact your administrator
            </Text>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ClientAuth;

